import type { ChangeEvent } from "react";
import { Paperclip, X } from "@phosphor-icons/react";
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_PARENT,
  attachmentRefusal,
  formatAttachmentBytes,
  type AttachmentUploadFailure
} from "../data/attachmentQueries";

/**
 * Choosing the files that go with a message or a comment, and refusing the ones that cannot.
 *
 * plan.md sections 5.2 and 5.3. One component for both surfaces, because the chat composer and the
 * request comment composer are choosing for the same table through the same gateway.
 *
 * **It holds no files of its own.** The owning form holds them, because the send has to read them
 * after the message lands and because a partial failure leaves exactly the files that did not land
 * still selected. A component that owned the list would have to be told to put three of four back.
 *
 * **The five-file ceiling is counted here and enforced in the database.** plan.md section 9, "Cost
 * controls", caps an ärende at five attachments, and `public.begin_attachment_upload` counts
 * `pending` and `available` rows against the same parent and raises `PORTAL_ATTACHMENT_LIMIT_REACHED`
 * at five. Each send creates a new message or a new comment, so the parent is new and the composer's
 * own count is the whole of the browser's half. The one case where that is not obvious is a retry
 * after a partial failure: the files that landed are on the parent already, the parent is not reused,
 * and the retry sends a new message with the remaining files. The count is therefore never stale.
 *
 * **Two refusals happen before anything is sent, and they name the file.** `app/lib/errors.ts` is
 * deliberately not where those sentences live: its own opening rule is that the submitted value is
 * never part of the message, which is right for a refusal about an action and wrong for one shown
 * beside a file the person is still holding. Whether the bytes really are a PDF is a question only
 * `worker/src/files/signatures.ts` can answer, and it does, so the browser's copy of the allow-list
 * is a courtesy that saves a 20 MiB upload on a phone and never a boundary.
 *
 * The picker is a real `<input type="file">` with a `<label>` in front of it. The input is clipped
 * rather than hidden, so it keeps its place in the tab order and `setInputFiles` can still reach it,
 * and the focus ring is drawn on the label through `:focus-within`. A `<button>` that clicked a
 * hidden input would move the focus ring off the thing that actually has focus.
 */

export interface AttachmentComposerProps {
  /** Unique per composer instance, because two of these can be on one page. */
  inputId: string;
  files: readonly File[];
  onChange: (next: File[]) => void;
  /** The read-only window, or a send in flight. The same state that disables the text field. */
  disabled: boolean;
  /**
   * What the server said about each file on the last send, matched by the `File` object itself.
   *
   * Identity rather than name: two files chosen from different folders can share a name, and the
   * owning form hands back the very objects it tried to send.
   */
  sendFailures?: readonly AttachmentUploadFailure[];
}

/** Same file chosen twice from the same picker. Three fields is what a browser can tell apart. */
function isSameFile(left: File, right: File): boolean {
  return (
    left.name === right.name &&
    left.size === right.size &&
    left.lastModified === right.lastModified
  );
}

/**
 * The paperclip on its own, split out on 2026-09-24 for the second build round, so the chat and the
 * request comment can put it at the start of one row the way `prototype/kund/pages.js` `composer()`
 * draws it: attach, write, send. `compact` draws the label as a 44 px icon with its name kept for a
 * screen reader, and keeps the file-count sentence out of sight until a file is chosen, because
 * "0 av 5 filer" under every empty composer is the kind of line design.md section 3, rule 2 removes.
 * The sentence stays in the document either way, so a screen reader still hears the ceiling.
 */
export function AttachmentPicker({
  inputId,
  files,
  onChange,
  disabled,
  compact = false
}: Omit<AttachmentComposerProps, "sendFailures"> & { compact?: boolean }) {
  const full = files.length >= MAX_ATTACHMENTS_PER_PARENT;

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const chosen = Array.from(event.target.files ?? []);
    const next = [...files];

    for (const file of chosen) {
      if (next.length >= MAX_ATTACHMENTS_PER_PARENT) {
        break;
      }
      if (next.some((existing) => isSameFile(existing, file))) {
        continue;
      }
      next.push(file);
    }

    /*
      Cleared so that choosing the same file again after removing it fires `change` a second time.
      An input whose value is unchanged is silent, and the removal happens in this component's own
      state rather than in the input, so without this the second choice would do nothing at all.
    */
    event.target.value = "";
    onChange(next);
  }

  return (
    <div
      /*
        The disabled state is carried on the wrapper because the control a person sees is a label and
        the thing that is actually disabled is the clipped input behind it. A label has no disabled
        state of its own, so without this the picker would look live while refusing every click.
      */
      className={`attachment-pick-row${compact ? " is-compact" : ""}${disabled || full ? " is-disabled" : ""}`}
    >
      <label className={`attachment-pick${compact ? " is-icon" : ""}`} htmlFor={inputId}>
        <Paperclip size={compact ? 20 : 16} aria-hidden="true" />
        <span className={compact ? "visually-hidden" : undefined}>Bifoga fil</span>
      </label>
      <input
        id={inputId}
        className="visually-hidden"
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        disabled={disabled || full}
        onChange={onPick}
        data-testid="attachment-input"
      />

      {/*
        One sentence, and which one depends on whether there is room. `role="status"` rather than an
        alert: reaching the ceiling is a state and not a fault, and it is reached by the person's own
        act a moment earlier.
      */}
      <p
        className={compact && files.length === 0 ? "visually-hidden" : "attachment-hint"}
        role="status"
        data-testid="attachment-hint"
      >
        {full
          ? `Fem filer är taket per meddelande. Ta bort en fil om ni vill byta ut den.`
          : `${files.length} av ${MAX_ATTACHMENTS_PER_PARENT} filer. Högst 20 MB per fil.`}
      </p>
    </div>
  );
}

/** The files chosen so far, each with its way out and, when there is one, its refusal. */
export function AttachmentChosen({
  inputId,
  files,
  onChange,
  disabled,
  sendFailures = []
}: AttachmentComposerProps) {
  function remove(file: File) {
    onChange(files.filter((existing) => existing !== file));
    // Focus has just left with the button that was removed. The picker is where the next act is.
    document.getElementById(inputId)?.focus();
  }

  if (files.length === 0) {
    return null;
  }

  return (
    <ul className="attachment-chosen" aria-label="Valda filer">
      {files.map((file) => {
        const refusal = attachmentRefusal(file);
        const sent = sendFailures.find((entry) => entry.file === file);

        return (
          <li
            key={`${file.name}-${file.size}-${file.lastModified}`}
            className={refusal || sent ? "attachment-chosen-row is-refused" : "attachment-chosen-row"}
            data-testid="attachment-chosen-row"
          >
            <div className="attachment-chosen-text">
              <strong>{file.name}</strong>
              <span>{formatAttachmentBytes(file.size)}</span>
            </div>

            <button
              type="button"
              className="icon-button"
              onClick={() => remove(file)}
              disabled={disabled}
              aria-label={`Ta bort ${file.name}`}
              data-testid="attachment-remove"
            >
              <X size={16} aria-hidden="true" />
            </button>

            {/*
              The browser's own refusal first, because it is the one the person can act on
              immediately, and the server's answer from the last send underneath it. Both name
              this file and neither is `app/lib/errors.ts`'s job.
            */}
            {refusal ? (
              <p className="attachment-refusal" data-testid="attachment-refusal">
                {refusal}
              </p>
            ) : null}
            {!refusal && sent ? (
              <p className="attachment-refusal" role="alert" data-testid="attachment-send-error">
                {sent.failure.message}
              </p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** The picker and the chosen files together, for a form that has room for both: Nytt ärende. */
export function AttachmentComposer(props: AttachmentComposerProps) {
  const full = props.files.length >= MAX_ATTACHMENTS_PER_PARENT;

  return (
    <div
      className={props.disabled || full ? "attachment-composer is-disabled" : "attachment-composer"}
      data-testid="attachment-composer"
    >
      <AttachmentPicker
        inputId={props.inputId}
        files={props.files}
        onChange={props.onChange}
        disabled={props.disabled}
      />
      <AttachmentChosen {...props} />
    </div>
  );
}
